import { newsList } from "@/mocks/news";
import { worksList } from "@/mocks/works";
import { actorsList } from "@/mocks/actors";
import type { Actor, NewsItem, Work } from "@/types/content";

export const allNews = newsList as NewsItem[];
export const allWorks = worksList as Work[];
export const allActors = actorsList as Actor[];

export function getNewsById(id: string): NewsItem | undefined {
  return allNews.find((n) => n.id === id);
}

export function getWorkById(id: string): Work | undefined {
  return allWorks.find((w) => w.id === id);
}

export function getActorById(id: string): Actor | undefined {
  return allActors.find((a) => a.id === id);
}

export function getNewsByIds(ids: string[]): NewsItem[] {
  return ids.map((id) => getNewsById(id)).filter((n): n is NewsItem => Boolean(n));
}

export function getWorksByIds(ids: string[]): Work[] {
  return ids.map((id) => getWorkById(id)).filter((w): w is Work => Boolean(w));
}

export function getRelatedWorks(work: Work, limit = 3): Work[] {
  const others = allWorks.filter((w) => w.id !== work.id);
  const scored = others
    .map((w) => {
      let score = 0;
      if (w.type === work.type) score += 2;
      score += w.cast.filter((c) => work.cast.includes(c)).length * 2;
      score += w.tags.filter((t) => work.tags.includes(t)).length;
      return { work: w, score };
    })
    .sort((a, b) => b.score - a.score || b.work.popularity - a.work.popularity);
  return scored.slice(0, limit).map((s) => s.work);
}

export function formatViews(views: number): string {
  if (views >= 10000) return `${(views / 10000).toFixed(1)}万`;
  return `${views}`;
}

export function formatDate(date: string): string {
  const [y, m, d] = date.split("-");
  return `${y}年${Number(m)}月${Number(d)}日`;
}

export interface SearchResult {
  news: NewsItem[];
  works: Work[];
  actors: Actor[];
}

export function searchContent(keyword: string): SearchResult {
  const q = keyword.trim().toLowerCase();
  if (!q) return { news: [], works: [], actors: [] };
  const match = (text: string) => text.toLowerCase().includes(q);
  return {
    news: allNews.filter(
      (n) => match(n.title) || match(n.excerpt) || n.tags.some(match) || match(n.category),
    ),
    works: allWorks.filter(
      (w) =>
        match(w.title) ||
        match(w.synopsis) ||
        w.tags.some(match) ||
        match(w.type) ||
        w.cast.some(match) ||
        match(w.director),
    ),
    actors: allActors.filter((a) => match(a.name) || match(a.bio) || match(a.role)),
  };
}